import {
  restResourceResolver,
  batchRestResourceResolver,
} from './restResourceResolver/restResourceResolver';
import {
  announcementsResolver,
  dismissAnnouncement,
} from './resolvers/announcements';
import {
  uploadCardCover,
  updateCardCover,
  createCardTemplate,
  copyCard,
  deleteCard,
} from './resolvers/card';
import {
  addChecklist,
  deleteChecklist,
  updateChecklistName,
  updateChecklistPos,
} from './resolvers/checklist';
import {
  addCheckItem,
  deleteCheckItem,
  updateCheckItemName,
  updateCheckItemPos,
  updateCheckItemState,
} from './resolvers/checkItem';
import { emailProviderResolver } from './resolvers/emailProvider';
import {
  addOneTimeMessagesDismissed,
  updateUserLocale,
  deleteOneTimeMessagesDismissed,
  prepMemberForAtlassianAccountOnboarding,
  prepMemberForEmailHygiene,
  memberAgreementsResolver,
  memberAtlassianOrganizationsResolver,
  acceptDeveloperTerms,
  updateMemberAvatarSource,
  uploadMemberAvatar,
  addCampaign,
  updateCampaign,
  resendVerificationEmail,
  changeMemberEmail,
} from './resolvers/member';
import {
  addFreeTrial,
  applyBCDiscount,
  organizationOwnedPluginsResolver,
  organizationStatsResolver,
  organizationCardStatsResolver,
  organizationLabelNamesResolver,
  organizationNewBillableGuestsResolver,
  createOrganization,
  copyBoardToOrg,
  deleteOrganizationLogo,
  addMembersToOrg,
  updateOrganization,
  uploadOrganizationImage,
} from './resolvers/organization';
import {
  salesTaxResolver,
  salesTaxForSubscriberResolver,
  createBusinessClassPaidAccount,
  createGoldPaidAccount,
  certCaptureTokenResolver,
  activateGoldCredit,
  paidAccountCustomTypeResolverFactory,
  reactivateBusinessClassPaidAccount,
  reactivateGoldPaidAccount,
  updateCreditCardFactory,
  updatePaidProductFactory,
  updateBillingContactDetailsFactory,
  updateInvoiceDetailsFactory,
  cancelPaidAccountFactory,
} from './resolvers/paidAccount';
import {
  publicPluginsResolver,
  pluginCategoriesResolver,
} from './resolvers/publicPlugin';
import { unsplashPhotosResolver } from './resolvers/unsplash';

import {
  enablePlugin,
  updateBoardOrg,
  updateBoardVisibility,
  boardExportResolver,
  startBoardExport,
  statsResolver,
} from './resolvers/board';
import {
  createPlugin,
  deletePlugin,
  updatePlugin,
  createPluginListing,
  updatePluginListing,
  deletePluginListing,
} from './resolvers/plugin';
import {
  templateCategoriesResolver,
  templateLanguagesResolver,
  templateGalleryResolver,
} from './resolvers/template';
import {
  enterpriseClaimableOrganizationsResolver,
  transferrableDataForOrganizationResolver,
  claimOrganization,
  linkEnterpriseWithAtlassianOrganization,
} from './resolvers/enterprise';
import { Resolvers } from 'apollo-client';

export const resolvers = {
  Query: {
    announcements: announcementsResolver,
    board: restResourceResolver,
    boards: batchRestResourceResolver,
    card: restResourceResolver,
    cards: batchRestResourceResolver,
    certCaptureToken: certCaptureTokenResolver,
    checklist: restResourceResolver,
    checklists: batchRestResourceResolver,
    list: restResourceResolver,
    lists: batchRestResourceResolver,
    emailProvider: emailProviderResolver,
    member: restResourceResolver,
    members: batchRestResourceResolver,
    organization: restResourceResolver,
    organizations: batchRestResourceResolver,
    label: restResourceResolver,
    labels: batchRestResourceResolver,
    enterprise: restResourceResolver,
    enterprises: batchRestResourceResolver,
    publicPlugins: publicPluginsResolver,
    salesTax: salesTaxResolver,
    salesTaxForSubscriber: salesTaxForSubscriberResolver,
    search: restResourceResolver,
    plugin: restResourceResolver,
    pluginCategories: pluginCategoriesResolver,
    unsplashPhotos: unsplashPhotosResolver,
    templateCategories: templateCategoriesResolver,
    templateLanguages: templateLanguagesResolver,
    templateGallery: templateGalleryResolver,
  },
  Mutation: {
    addChecklist,
    addOneTimeMessagesDismissed,
    createOrganization,
    copyBoardToOrg,
    updateBoardOrg,
    updateBoardVisibility,
    dismissAnnouncement,
    deleteChecklist,
    updateChecklistName,
    updateChecklistPos,
    addCheckItem,
    deleteCheckItem,
    addCampaign,
    updateCampaign,
    updateCheckItemName,
    updateCheckItemPos,
    updateCheckItemState,
    updateUserLocale,
    updateOrganization,
    prepMemberForAtlassianAccountOnboarding,
    prepMemberForEmailHygiene,
    deleteOneTimeMessagesDismissed,
    createPlugin,
    deletePlugin,
    enablePlugin,
    updatePlugin,
    createPluginListing,
    updatePluginListing,
    deletePluginListing,
    acceptDeveloperTerms,
    updateMemberAvatarSource,
    updateCardCover,
    uploadCardCover,
    uploadMemberAvatar,
    changeMemberEmail,
    resendVerificationEmail,
    addMembersToOrg,
    createCardTemplate,
    copyCard,
    deleteCard,
    startBoardExport,
    claimOrganization,
    linkEnterpriseWithAtlassianOrganization,
    createBusinessClassPaidAccount,
    createGoldPaidAccount,
    activateGoldCredit,
    reactivateBusinessClassPaidAccount,
    reactivateGoldPaidAccount,
    updateBusinessClassCreditCard: updateCreditCardFactory('organizations'),
    updateGoldCreditCard: updateCreditCardFactory('members'),
    updateBusinessClassPaidProduct: updatePaidProductFactory('organizations'),
    updateGoldPaidProduct: updatePaidProductFactory('members'),
    updateBusinessClassBillingContactDetails: updateBillingContactDetailsFactory(
      'organizations',
    ),
    updateGoldBillingContactDetails: updateBillingContactDetailsFactory(
      'members',
    ),
    cancelBusinessClassPaidAccount: cancelPaidAccountFactory('organizations'),
    cancelGoldPaidAccount: cancelPaidAccountFactory('members'),
    updateBusinessClassBillingInvoiceDetails: updateInvoiceDetailsFactory(
      'organizations',
    ),
    updateGoldBillingInvoiceDetails: updateInvoiceDetailsFactory('members'),
    uploadOrganizationImage,
    deleteOrganizationLogo,
    addFreeTrial,
    applyBCDiscount,
  },

  // Custom type resolvers
  Board: {
    export: boardExportResolver,
    exports: boardExportResolver,
    stats: statsResolver,
  },

  Enterprise: {
    claimableOrganizations: enterpriseClaimableOrganizationsResolver,
    transferrableData: transferrableDataForOrganizationResolver,
  },

  Member: {
    agreements: memberAgreementsResolver,
    atlassianOrganizations: memberAtlassianOrganizationsResolver,
    paidAccount: paidAccountCustomTypeResolverFactory('members'),
  },

  Organization: {
    ownedPlugins: organizationOwnedPluginsResolver,
    stats: organizationStatsResolver,
    newBillableGuests: organizationNewBillableGuestsResolver,
    paidAccount: paidAccountCustomTypeResolverFactory('organizations'),
  },

  Organization_Stats: {
    cards: organizationCardStatsResolver,
    labelNames: organizationLabelNamesResolver,
  },
} as Resolvers;
